import type { GetRowIdParams } from 'ag-grid-community';
import { ISearchResponse } from 'apiConnector/blotters';
import { useCommonConnect } from 'components/Blotters/Trades/Grid/connect/useCommonConnect';
import { BlotterGridDispatchProps, BlotterGridStateProps } from 'components/shared/Blotter/Grid/blotter.model';
import { BlotterRequestByBlotterType } from 'epic/blotters/tradesBlotter/tradesBlottersEpic';
import { useAppSelector } from 'hooks/useAppSelector';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { useCallback } from 'react';
import { createSelector } from 'reselect';
import {
  tradesBlottersItemsFetchingFailed,
  tradesBlottersSetLoading,
} from 'state/blotters/trades/tradesBlotters.actions';
import {
  selectCommonTradesBlottersActivityFilter,
  selectCommonTradesBlottersEndDateFilter,
  selectCommonTradesBlottersQuickSearchFilter,
  selectCommonTradesBlottersStartDateFilter,
  selectTradesBlottersPreferencePageSize,
} from 'state/blotters/trades/tradesBlotters.selector';
import { TradesBlottersItemByBlotterType } from 'state/blotters/trades/types/blotterItem';
import {
  BlotterType,
  FilterBlotterType,
  MasterDetailsBlotterType,
  NoMasterDetailsBlotterType,
  PageBlotterType,
  PreferenceBlotterType,
} from 'state/blotters/trades/types/types';
import { toEndOfDay, toStartOfDay } from 'utils/core/date';
import { getFrontLogger } from 'utils/loggers/FrontLogger';

type OwnProps<T extends BlotterType> = {
  blotter: T;
  idKey: keyof TradesBlottersItemByBlotterType<T>;
};
type StateProps<T extends BlotterType> = BlotterGridStateProps<TradesBlottersItemByBlotterType<T>> & {
  blotterRequest: BlotterRequestByBlotterType<T>;
  getRowId: (p: GetRowIdParams) => string;
};
type DispatchProps<T extends BlotterType> = BlotterGridDispatchProps<TradesBlottersItemByBlotterType<T>> & {
  onItemsReceived: (response: ISearchResponse<TradesBlottersItemByBlotterType<T>>) => void;
  onItemsRequested: () => void;
  onRequestFailed: (error: any) => void;
};

type ServerSideConnectProps<T extends BlotterType> = OwnProps<T> & StateProps<T> & DispatchProps<T>;

const selectBlotterRequest = createSelector(
  selectCommonTradesBlottersQuickSearchFilter,
  selectCommonTradesBlottersActivityFilter,
  selectTradesBlottersPreferencePageSize,
  selectCommonTradesBlottersStartDateFilter,
  selectCommonTradesBlottersEndDateFilter,
  (fullTextSearch, activityScope, size, startDate, endDate) => {
    return {
      activityScope,
      fullTextSearch,
      size,
      startDate: toStartOfDay(startDate).toISOString(),
      endDate: toEndOfDay(endDate).toISOString(),
    };
  },
);

export function useServerSideConnect<
  T extends PageBlotterType & Exclude<PreferenceBlotterType, MasterDetailsBlotterType> & FilterBlotterType,
>(ownProps: OwnProps<T>): ServerSideConnectProps<T> {
  const commonProps = useCommonConnect(ownProps);

  const blotterRequest = useAppSelector(state =>
    selectBlotterRequest(state, ownProps.blotter),
  ) as BlotterRequestByBlotterType<T>;

  const dispatch = useThunkDispatch();

  const onItemsReceived = useCallback(() => {
    dispatch(tradesBlottersSetLoading<NoMasterDetailsBlotterType>(ownProps.blotter, false, undefined));
  }, [dispatch, ownProps.blotter]);

  const onItemsRequested = useCallback(() => {
    dispatch(tradesBlottersSetLoading<NoMasterDetailsBlotterType>(ownProps.blotter, true, undefined));
  }, [dispatch, ownProps.blotter]);

  const onRequestFailed = useCallback(
    (err: Error) => {
      getFrontLogger().error('Failed to retrieve data for blotter {blotter} : {error}', ownProps.blotter, err);
      dispatch(tradesBlottersItemsFetchingFailed<NoMasterDetailsBlotterType, undefined>(ownProps.blotter, undefined));
    },
    [dispatch, ownProps.blotter],
  );

  return {
    ...ownProps,
    ...commonProps,
    blotterRequest,
    onItemsReceived,
    onItemsRequested,
    onRequestFailed,
  };
}
